/**
 * CSV lesen — für den Import der Kontakte (ADR-0016).
 *
 * **Warum von Hand und nicht als Abhängigkeit.** Was hier gebraucht wird, ist
 * ein Leser nach RFC 4180 mit zwei Zugeständnissen an die Wirklichkeit:
 * Semikolon als Trenner, weil Excel auf deutschen Rechnern so schreibt, und
 * ein BOM am Anfang, weil Excel eines setzt. Das ist überschaubar und hier
 * vollständig geprüft. Eine Bibliothek dafür wäre eine Abhängigkeit in einem
 * Pfad, der fremde Dateien liest — dafür ist der Gegenwert zu klein.
 *
 * **Was der Leser kann:** Anführungszeichen, verdoppelte Anführungszeichen
 * darin, Trenner und Zeilenumbrüche innerhalb von Anführungszeichen, CRLF und
 * LF gemischt, BOM.
 *
 * **Was er nicht kann:** Kommentarzeilen, mehrzeilige Kopfbereiche, andere
 * Zeichensätze als das, was der Browser als Text liefert. Das ist bewusst —
 * jede weitere Sonderregel ist eine Annahme über fremde Dateien.
 */
/** Trenner, die erkannt werden. Reihenfolge ist die Vorliebe bei Gleichstand. */
const TRENNER = [';', ',', '\t'];
/**
 * Rät den Trenner an der Kopfzeile.
 *
 * Gezählt wird **außerhalb** von Anführungszeichen — ein Semikolon in einem
 * Firmennamen soll nicht mitzählen. Bei Gleichstand gewinnt die Reihenfolge
 * oben; ohne jeden Treffer bleibt es beim Semikolon, dann hat die Datei genau
 * eine Spalte und die Wahl ist gleichgültig.
 */
export function trennerRaten(text) {
    const kopf = ersteZeile(text);
    let bester = ';';
    let meiste = 0;
    for (const kandidat of TRENNER) {
        const anzahl = zaehleAusserhalb(kopf, kandidat);
        if (anzahl > meiste) {
            meiste = anzahl;
            bester = kandidat;
        }
    }
    return bester;
}
function ersteZeile(text) {
    let inAnfuehrung = false;
    for (let i = 0; i < text.length; i += 1) {
        const zeichen = text[i];
        if (zeichen === '"') {
            // Zwei Anfuehrungszeichen im Feld sind eines im Inhalt — der Zustand
            // bleibt, und das zweite wird uebersprungen.
            if (inAnfuehrung && text[i + 1] === '"') {
                i += 1;
                continue;
            }
            inAnfuehrung = !inAnfuehrung;
            continue;
        }
        if (!inAnfuehrung && (zeichen === '\n' || zeichen === '\r'))
            return text.slice(0, i);
    }
    return text;
}
function zaehleAusserhalb(zeile, trenner) {
    let inAnfuehrung = false;
    let anzahl = 0;
    for (let i = 0; i < zeile.length; i += 1) {
        const zeichen = zeile[i];
        if (zeichen === '"') {
            if (inAnfuehrung && zeile[i + 1] === '"') {
                i += 1;
                continue;
            }
            inAnfuehrung = !inAnfuehrung;
            continue;
        }
        if (!inAnfuehrung && zeichen === trenner)
            anzahl += 1;
    }
    return anzahl;
}
/** Liest den Text in Zeilen und Felder. Der Trenner wird geraten, wenn keiner kommt. */
export function csvLesen(roh, trenner) {
    // BOM weg: Excel setzt eines, und sonst hieße die erste Spalte „﻿Firma".
    const text = roh.replace(/^﻿/, '');
    const t = trenner ?? trennerRaten(text);
    const zeilen = [];
    let feld = '';
    let zeile = [];
    let inAnfuehrung = false;
    const feldEnde = () => {
        zeile.push(feld);
        feld = '';
    };
    const zeilenEnde = () => {
        feldEnde();
        zeilen.push(zeile);
        zeile = [];
    };
    for (let i = 0; i < text.length; i += 1) {
        const zeichen = text[i];
        if (inAnfuehrung) {
            if (zeichen === '"') {
                // Zwei Anführungszeichen sind eines im Inhalt.
                if (text[i + 1] === '"') {
                    feld += '"';
                    i += 1;
                }
                else {
                    inAnfuehrung = false;
                }
            }
            else {
                feld += zeichen;
            }
            continue;
        }
        if (zeichen === '"' && feld === '') {
            inAnfuehrung = true;
            continue;
        }
        if (zeichen === t) {
            feldEnde();
            continue;
        }
        if (zeichen === '\r') {
            // CRLF und ein einzelnes CR gelten beide als Zeilenende.
            if (text[i + 1] === '\n')
                i += 1;
            zeilenEnde();
            continue;
        }
        if (zeichen === '\n') {
            zeilenEnde();
            continue;
        }
        feld += zeichen;
    }
    // Die letzte Zeile zählt auch ohne abschließenden Umbruch.
    if (feld !== '' || zeile.length > 0)
        zeilenEnde();
    // Leerzeilen fliegen raus — eine Datei endet oft mit einer.
    const echte = zeilen.filter((z) => z.some((f) => f.trim() !== ''));
    const [kopf = [], ...rest] = echte;
    const spalten = kopf.map((name, i) => {
        const sauber = name.trim();
        return sauber === '' ? `Spalte ${i + 1}` : sauber;
    });
    // Auf die Spaltenzahl bringen: Kürzere Zeilen bekommen leere Felder, längere
    // werden nicht abgeschnitten — sonst verschwänden Daten still.
    const breite = Math.max(spalten.length, ...rest.map((z) => z.length), 0);
    while (spalten.length < breite)
        spalten.push(`Spalte ${spalten.length + 1}`);
    return {
        spalten,
        zeilen: rest.map((z) => {
            const voll = [...z];
            while (voll.length < breite)
                voll.push('');
            return voll;
        }),
        trenner: t,
    };
}
//# sourceMappingURL=csv.js.map